import { ActionIcon } from "@mantine/core";
import { ArrowLeftRight } from "lucide-react";

import { ImageRefsSlot, type ImageRefs } from "../../../platform/media/ImageRefsSlot";
import classes from "../../../platform/media/slots.module.css";
import { ElementsSlot, type ElementsProps } from "./ElementsSlot";
import { AudioRefsSlot } from "./AudioRefsSlot";
import type { MediaRefs } from "./MediaRefsSlot";
import { VideoRefSlot } from "./VideoRefSlot";
import { EndFrameSlot } from "./EndFrameSlot";
import type { Frame } from "./FrameSlot";
import { StartFrameSlot } from "./StartFrameSlot";

export interface FrameSlotsProps {
  /** The start frame, when the catalog offers a video from an image; else Coming soon. */
  start: Frame | undefined;
  /** The end frame, when the model takes one; else live leaves it and the swap out. */
  end: Frame | undefined;
  onSwap: () => void;
  /**
   * The keyframes between the frames, where a mode of the model takes them (Kling 4.0's, spec
   * web/keyframes-slot); else none. Live's Image refs tile, after the end frame.
   */
  keyframes?: ImageRefs | undefined;
  /** The reference images, where a mode of the model takes them (#232); else none. */
  refs?: ImageRefs | undefined;
  /** The elements, where a mode of the model takes them (#229); else Coming soon. */
  elements?: ElementsProps | undefined;
  /** The reference videos and audios, where a mode takes them (#260); else none. */
  videos?: MediaRefs | undefined;
  audios?: MediaRefs | undefined;
}

/**
 * The video composer's frame and element slots, above the prompt, in live's order: the start
 * frame, the swap arrow, the end frame (then any keyframes, ours) and elements. Each slot is its
 * own file.
 */
export function FrameSlots(props: FrameSlotsProps) {
  const { start, end, onSwap, keyframes, refs, elements, videos, audios } = props;
  return (
    <div className={classes.slots} data-slots>
      <StartFrameSlot start={start} />
      {start === undefined || end === undefined ? null : (
        // Live's swap: the frames change places; with neither chosen there is nothing to swap.
        <ActionIcon
          variant="transparent"
          className={classes.swap}
          aria-label="Swap the start and end frames"
          disabled={
            (start.view === null && end.view === null) ||
            start.disabled === true ||
            end.disabled === true
          }
          onClick={onSwap}
        >
          <ArrowLeftRight size={16} strokeWidth={1.5} aria-hidden />
        </ActionIcon>
      )}
      {end === undefined ? null : <EndFrameSlot end={end} />}
      {keyframes === undefined ? null : <ImageRefsSlot refs={keyframes} />}
      <ElementsSlot elements={elements} />
      {refs === undefined ? null : <ImageRefsSlot refs={refs} />}
      {videos === undefined ? null : <VideoRefSlot refs={videos} />}
      {audios === undefined ? null : <AudioRefsSlot refs={audios} />}
    </div>
  );
}
